import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Eye } from 'lucide-react';

// Ownership rule (Nic 2026-09-29): nobody acts on a lead or client unless it
// is assigned to them. Managers can VIEW anything and REASSIGN, nothing else;
// unowned items can be claimed by anyone who works the board. The server
// enforces this (403 code 'not_owner'); these helpers keep the UI from
// offering actions the server would refuse.

// True only when both ids exist and match. A missing viewer id never owns.
export const isOwnedBy = (ownerUserId, viewerId) =>
  !!ownerUserId && !!viewerId && String(ownerUserId) === String(viewerId);

const firstNameOf = (name) => (name ? String(name).trim().split(/\s+/)[0] : '');

// The same words the server sends back, so a refusal reads identically
// whichever side catches it.
export const notYoursMessage = (ownerName) => {
  const first = firstNameOf(ownerName);
  return `This is on ${first ? `${first}'s` : "someone else's"} list. Reassign it to yourself first.`;
};

// The read-only line that stands in for the action controls when the viewer
// does not own the item. Light blue like every informational note (Nic
// 2026-10-05: info has to pop).
// viewOnly (Nic 2026-10-23): an admin or a viewer, who never works people,
// is told so instead of being told to reassign it to themselves.
export const VIEW_ONLY_TITLE = 'View only';
export const NotYoursNote = ({ ownerName, noun = 'board', className = '', viewOnly = false }) => {
  const first = firstNameOf(ownerName);
  return (
    <div
      className={`flex items-start gap-2 rounded-xl bg-sky-50 px-3 py-2.5 text-xs font-medium text-sky-800 ring-1 ring-sky-200 dark:!bg-sky-500/10 dark:text-sky-200 dark:ring-sky-500/30 ${className}`}
    >
      <Eye size={14} className="mt-px shrink-0" />
      <span>
        {viewOnly
          ? `View only. You can see everything; ${first || 'their owner'} does the work.`
          : `${first ? `On ${first}'s ${noun}.` : `On someone else's ${noun}.`} Reassign it to yourself to work it.`}
      </span>
    </div>
  );
};

// Someone else's lead, picked from the people search (Nic 2026-10-14): one
// clean card saying who is working them and who can move them, instead of
// switching tabs to nothing.
export const NotYoursDialog = ({ person, onClose }) => {
  useEffect(() => {
    if (!person) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [person, onClose]);
  if (!person) return null;
  const name =
    `${person.firstName || ''} ${person.lastName || ''}`.trim() || 'this lead';
  const owner = person.ownerName || 'Another Sales Rep';
  const ownerFirst = firstNameOf(person.ownerName);
  // Nic 2026-10-14: the rep's name up top, then who they are working, then
  // one short line on who can move them. No icon.
  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-[2px]"
      onMouseDown={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="not-yours-title"
        onMouseDown={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-[0_20px_60px_rgba(15,23,42,0.3)] dark:!bg-surface-elevated dark:ring-1 dark:ring-white/10"
      >
        <h3
          id="not-yours-title"
          className="mb-0.5 text-base font-bold text-slate-800 dark:text-slate-100"
        >
          {owner}
        </h3>
        <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
          is working{' '}
          <span className="font-semibold text-slate-700 dark:text-slate-200">
            {name}
          </span>
        </p>
        <p className="mb-5 rounded-xl bg-slate-50 px-3 py-2.5 text-xs leading-relaxed text-slate-500 dark:!bg-white/5 dark:text-slate-400">
          To work them, {ownerFirst || 'their rep'} or an Onboarding Manager
          can move them to you.
        </p>
        <button
          type="button"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          onClick={onClose}
          className="w-full rounded-xl bg-slate-900 py-2.5 text-sm font-semibold text-white outline-none hover:bg-slate-700 focus:outline-none dark:!bg-slate-100 dark:!text-slate-900"
        >
          Got it
        </button>
      </div>
    </div>,
    document.body,
  );
};
